import {User} from '../user';
import {Document} from '../document';
import {PartnerContact} from './partner-contact';
import {CompanyRole} from '../../constants/company-role';
import {Currency} from '../currency';
import {AbstractDocumentsModel} from '../abstract-documents-model';
import {AdvertisingPlatform} from '../advertising-platform';
import {PartnerAdvertisingPlatform} from './partner-advertising-platform';

export class Partner extends AbstractDocumentsModel {
  accountManagerId: number | null = null;
  accountManager: User | null = null;
  accountType: string | null = null;
  status: string | null = null;
  advertisingPlatform: AdvertisingPlatform | null = null;
  prospect: boolean = false;
  companyStatus: string | null = null;
  companyName: string | null = null;
  companyRegistrationNumber: string | null = null;
  companyVatNumber: string | null = null;
  langs: string[] | null = null;
  country: string | null = null;
  address: string | null = null;
  zipCode: string | null = null;
  state: string | null = null;
  city: string | null = null;
  email: string | null = null;
  phoneNumber: string | null = null;
  prospectOrigin: string | null = null;
  typology: string | null = null;
  verticals: number[] = [];
  geographicAreas: string[] = [];
  trafficSources: string | null = null;
  experience: string | null = null;
  estimatedMonthlyRevenue: number | null = null;
  websiteUrl: string | null = null;
  paymentThreshold: number | null = null;
  paymentTerms: string = 'net30';
  paymentCurrency: Currency | null = null;
  paymentMethod: string = 'wire';
  accountHolder: string | null = null;
  bankName: string | null = null;
  bankAddress: string | null = null;
  iban: string | null = null;
  swift: string | null = null;
  routingNumber: string | null = null;
  accountComplianceNote: string | null = null;

  credentialUrl!: string;
  credentialUsername!: string;
  credentialPassword!: string;

  image: Document | null = null;

  lastActivityAt: Date | null = null;

  allOffersAccess: boolean = true;
  subjectToVat: boolean = false;

  contacts!: Record<CompanyRole, PartnerContact>;

  advertisingPlatforms: PartnerAdvertisingPlatform[] = [];

  override deserialize(input: any): this {
    super.deserialize(input);

    if (input.accountManager) {
      this.accountManager = new User().deserialize(input.accountManager);
    }

    if (input.advertisingPlatform) {
      this.advertisingPlatform = new AdvertisingPlatform().deserialize(input.advertisingPlatform);
    }

    if (input.image) {
      this.image = new Document().deserialize(input.image);
    }

    this.contacts = {
      [CompanyRole.MAIN]: new PartnerContact(),
      [CompanyRole.SIGNATORY]: new PartnerContact(),
      [CompanyRole.SALES]: new PartnerContact(),
      [CompanyRole.TECHNICAL]: new PartnerContact(),
      [CompanyRole.ACCOUNTING]: new PartnerContact(),
    };

    if (input.contacts) {
      input.contacts.forEach((contact: any) => {
        const partnerContact = new PartnerContact().deserialize(contact);
        this.contacts[partnerContact.companyRole] = partnerContact;
      });
    }

    if (input.advertisingPlatforms) {
      this.advertisingPlatforms = [];
      input.advertisingPlatforms.forEach((data: any) => {
        this.advertisingPlatforms.push(new PartnerAdvertisingPlatform().deserialize(data));
      });
    }

    if (input.paymentCurrency) {
      this.paymentCurrency = new Currency().deserialize(input.paymentCurrency);
    }

    return this;
  }

  getMainContact(): PartnerContact {
    return this.contacts[CompanyRole.MAIN];
  }

  isAffiliate(): boolean {
    return this.accountType === 'affiliate';
  }

  isMediaBuy(): boolean {
    return this.accountType === 'mediabuy';
  }

  isGoogleAdvertiser(): boolean {
    return this.isMediaBuy() && this.advertisingPlatform?.identifier === 'google-ads';
  }

  isMetaAdvertiser(): boolean {
    return this.isMediaBuy() && this.advertisingPlatform?.identifier === 'meta-ads';
  }

  isMicrosoftAdvertiser(): boolean {
    return this.isMediaBuy() && this.advertisingPlatform?.identifier === 'microsoft-ads';
  }

  isXAdvertiser(): boolean {
    return this.isMediaBuy() && this.advertisingPlatform?.identifier === 'x-ads';
  }

  isTiktokAdvertiser(): boolean {
    return this.isMediaBuy() && this.advertisingPlatform?.identifier === 'tiktok-ads';
  }

  getAccountTypeClass(): string | null {
    switch (this.accountType) {
      case 'affiliate':
        return 'bg-primary';
      case 'mediabuy':
        return 'bg-info';
      case 'internal':
        return 'bg-secondary';
    }

    return null;
  }

  getStateClass(): string | null {
    switch (this.status) {
      case 'active':
        return 'bg-success';
      case 'rejected':
        return 'bg-danger';
      case 'pending':
        return 'bg-warning';
      case 'suspended':
        return 'bg-light text-dark';
      case 'closed':
        return 'bg-dark';
    }

    return null;
  }

  override toString(): string {
    let name: string = String(this.id);

    if (this.companyName) {
      name += ' - ' + this.companyName ;
    }

    return name.trim();
  }
}
